import React from 'react';
import { motion } from 'framer-motion';
import { FaUsers, FaCode, FaGlobe, FaRocket, FaLightbulb, FaHandshake, FaBrain } from 'react-icons/fa';
import BlurText from './ui/BlurText';

const AboutSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6 },
    },
  };

  return (
    <section id="about" className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 grid-pattern opacity-20"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-accent/20 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10 sm:mb-12 md:mb-16"
        >
          <motion.div
            className="inline-flex items-center space-x-2 glass-strong px-4 sm:px-6 py-2 sm:py-3 rounded-full mb-4 sm:mb-6"
            whileHover={{ scale: 1.05 }}
          >
            <FaUsers className="text-primary text-sm sm:text-base" />
            <span className="text-xs sm:text-sm font-mono">About SDC</span>
          </motion.div>

          <BlurText
            text="Who We Are"
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 text-center"
            childClassName="gradient-text"
            delay={150}
            animateBy="words"
            direction="top"
          />
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            A community of passionate developers, innovators, and tech enthusiasts building the future together
          </p>
        </motion.div>

        {/* Mission Statement */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 mb-20"
        >
          <motion.div variants={itemVariants} className="glass-strong rounded-2xl p-8 lg:p-10 border border-primary/20">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <FaRocket className="text-white text-xl" />
              </div>
              <h3 className="text-2xl font-bold">Our Mission</h3>
            </div>
            <p className="text-gray-300 leading-relaxed text-lg">
              To empower students with practical software development skills, foster innovation through
              collaborative projects, and bridge the gap between academic learning and industry requirements.
              We believe in learning by doing and growing together as a community.
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="glass-strong rounded-2xl p-8 lg:p-10 border border-accent/20">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-accent to-emerald flex items-center justify-center">
                <FaLightbulb className="text-white text-xl" />
              </div>
              <h3 className="text-2xl font-bold">Our Vision</h3>
            </div>
            <p className="text-gray-300 leading-relaxed text-lg">
              To become the leading platform for student developers to learn, collaborate, and innovate.
              We envision a future where every student has the skills and confidence to build amazing
              software that solves real-world problems and makes a positive impact.
            </p>
          </motion.div>
        </motion.div>

        {/* What We Do */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/30 text-accent text-sm font-medium mb-6">
              <span>Activities</span>
              <span className="w-1 h-1 rounded-full bg-accent"></span>
              <span className="text-emerald">Community Driven</span>
            </div>
            <div className="flex justify-center mb-4">
              <BlurText
                text="What We Do"
                className="text-4xl md:text-5xl font-bold text-center"
                childClassName="text-transparent bg-clip-text bg-gradient-to-r from-accent via-emerald to-primary"
                delay={150}
                animateBy="words"
                direction="top"
              />
            </div>
            <div className="mx-auto h-[2px] w-20 bg-gradient-to-r from-accent to-emerald rounded-full mb-6" />
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Building skills, fostering innovation, and growing together
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <ActivityCard
              icon={<FaCode />}
              title="Workshops"
              description="Regular coding workshops and tech talks on latest technologies"
              color="from-primary to-accent"
            />
            <ActivityCard
              icon={<FaHandshake />}
              title="Hackathons"
              description="Organize and participate in competitive coding events"
              color="from-accent to-emerald"
            />
            <ActivityCard
              icon={<FaBrain />}
              title="Mentorship"
              description="Peer-to-peer learning and guidance from experienced members"
              color="from-emerald to-primary"
            />
            <ActivityCard
              icon={<FaGlobe />}
              title="Open Source"
              description="Contribute to open source projects and build your portfolio"
              color="from-secondary to-primary"
            />
          </div>
        </motion.div>

        {/* Stats */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30 text-primary text-sm font-medium mb-6">
            <span>Our Impact</span>
            <span className="w-1 h-1 rounded-full bg-primary"></span>
            <span className="text-accent">Growing Strong</span>
          </div>
          <h3 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-emerald">
              By The Numbers
            </span>
          </h3>
          <div className="mx-auto h-[2px] w-20 bg-gradient-to-r from-primary via-accent to-emerald rounded-full" />
        </div>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-8"
        >
          <StatCard variants={itemVariants} number="50+" label="Members" />
          <StatCard variants={itemVariants} number="10+" label="Projects" />
          <StatCard variants={itemVariants} number="5+" label="Events" />
          <StatCard variants={itemVariants} number="20+" label="Tech Stacks" />
        </motion.div>

        {/* Call to Action */}
      </div>
    </section>
  );
};

const ActivityCard = ({ icon, title, description, color }) => (
  <motion.div
    className="group relative glass-strong rounded-3xl p-8 text-center border border-primary/20 hover:border-accent/60 transition-all duration-700 overflow-hidden"
    whileHover={{ y: -8, scale: 1.02 }}
  >
    {/* Gradient background on hover */}
    <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-emerald/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
    <div className="absolute -inset-px bg-gradient-to-br from-accent/20 to-emerald/20 rounded-3xl opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-700" />

    {/* Content */}
    <div className="relative z-10">
      <div className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br ${color} mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-700 shadow-lg`}>
        <div className="text-white text-3xl">{icon}</div>
      </div>
      <h4 className="text-xl font-bold text-light mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-accent group-hover:to-emerald transition-all duration-700">
        {title}
      </h4>
      <p className="text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors duration-700">
        {description}
      </p>

      {/* Animated underline */}
      <div className="mt-4 h-[2px] w-0 group-hover:w-full bg-gradient-to-r from-accent to-emerald rounded-full transition-all duration-700 mx-auto" />
    </div>

    {/* Shimmer effect */}
    <div className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-1000">
      <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 bg-gradient-to-r from-transparent via-white to-transparent skew-x-12" />
    </div>
  </motion.div>
);

const StatCard = ({ variants, number, label }) => (
  <motion.div
    variants={variants}
    className="group relative glass-strong rounded-3xl p-8 text-center border border-primary/20 hover:border-primary/60 transition-all duration-700 overflow-hidden"
    whileHover={{ scale: 1.05, y: -4 }}
  >
    {/* Animated gradient background */}
    <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
    <div className="absolute -inset-px bg-gradient-to-br from-primary/30 to-accent/30 rounded-3xl opacity-0 group-hover:opacity-100 blur-2xl transition-opacity duration-700" />

    {/* Floating orbs */}
    <div className="absolute top-0 right-0 w-24 h-24 bg-primary/20 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 animate-pulse" />
    <div className="absolute bottom-0 left-0 w-32 h-32 bg-accent/20 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-1000 animate-pulse" />

    {/* Content */}
    <div className="relative z-10">
      <div className="text-5xl md:text-6xl font-black mb-3 group-hover:scale-110 transition-transform duration-700">
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-emerald animate-gradient">
          {number}
        </span>
      </div>
      <div className="w-12 h-[2px] mx-auto mb-4 bg-gradient-to-r from-transparent via-primary to-transparent opacity-50" />
      <div className="text-sm font-bold font-mono text-accent uppercase tracking-widest group-hover:text-emerald transition-colors duration-700">
        {label}
      </div>
    </div>

    {/* Shimmer effect */}
    <div className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-1000">
      <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 bg-gradient-to-r from-transparent via-white to-transparent skew-x-12" />
    </div>
  </motion.div>
);

export default AboutSection;
